<template>
    <div id="stock-image-layout" class="content-layout padded">
        <div class="container">
            <div id="image-details" class="row">
                <div class="col-md-7 col-lg-8">
                    <StockImage ref="stockImage" :image="image" @loaded="startLoadQueue" @stockFromShootLoaded="loadSimilarStock"/>
                </div>
                <div class="col-md-5 col-lg-4">
                    <StockPricePicker ref="pricePicker" :image="image" @showContact="showContact" @loaded="loadStockInAlbum"/>
                </div>
            </div>
            <SimilarImages ref="similarStock" :image="image"/>
        </div>
        <b-modal id="modal-exclusive-use" centered hide-footer>
            <template #modal-title>
                {{ contactFormHeading }} for #{{ image.code }}
            </template>
            <form class="auth-form" @submit.prevent="sendExclusiveUseRequest" @keydown="contactForm.onKeydown($event)">
                <div class="form-row">
                    <div class="form-group col-sm-12">
                        <label class="required">Name</label>
                        <input v-model="contactForm.name" :class="{ 'is-invalid': contactForm.errors.has('name') }" type="text" name="email" class="form-control">
                        <has-error :form="contactForm" field="name"/>
                    </div>
                </div>
                <div class="form-row">
                    <div class="form-group col-sm-12">
                        <label class="required">Contact Email</label>
                        <input v-model="contactForm.email" :class="{ 'is-invalid': contactForm.errors.has('email') }" type="text" name="email" class="form-control">
                        <has-error :form="contactForm" field="email"/>
                    </div>
                </div>
                <div class="form-row">
                    <div class="form-group col-sm-12">
                        <label>Message</label>
                        <textarea v-model="contactForm.message" rows="4" :class="{ 'is-invalid': contactForm.errors.has('message') }" type="text" name="message" class="form-control"
                                  :placeholder="contactFormPlaceholder"
                        />
                        <has-error :form="contactForm" field="message"/>
                    </div>
                </div>
                <div class="form-row">
                    <div class="form-group col-sm-12">
                        <v-button variant="primary" class="float-right btn-lg" :loading="contactForm.busy">
                            Submit
                        </v-button>
                    </div>
                </div>
            </form>
        </b-modal>
    </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import Form from 'vform'
import StockImage from '~/components/stockImage/StockImage'
import SimilarImages from '~/components/stockImage/SimilarImages'
import StockPricePicker from '~/components/stockImage/StockPricePicker.vue'

export default {
    components: {
        StockPricePicker,
        SimilarImages,
        StockImage
    },
    validate ({ params }) {
        return /^TS\d{6}$/.test(params.image_id)
    },
    async asyncData ({
        error,
        params,
        redirect
    }) {
        try {
            const fetchedStock = await axios.get(`/stock-images/${params.image_id}`).then(rsp => rsp.data.data)

            if (fetchedStock) {
                if (!params.slug || params.slug !== fetchedStock.slug) {
                    redirect(302, fetchedStock.link)
                }

                return {
                    image: fetchedStock
                }
            } else {
                redirect('/')
            }
        } catch (err) {
            console.log(err)
            error({
                statusCode: 404,
                message: 'Page not found'
            })
        }
    },
    data () {
        return {
            imageId: this.$route.params.image_id,
            image: {},
            selected: {
                licence: null,
                size: null
            },
            inCartUpdatable: false,
            contactFormHeading: '',
            contactFormPlaceholder: '',
            contactForm: new Form({
                name: '',
                email: '',
                message: '',
                image_code: '',
                type: 'exclusive'
            }),
            loadQueue: {
                started: false,
                prices: false,
                inAlbum: false,
                similar: false
            }
        }
    },
    head () {
        const title = `${this.image.description ?? this.image.code} by ${this.image.contributor.name} - Truestock`
        const description = `#${this.image.code}: ${this.image.description} shot by ${this.image.contributor.name}`

        return {
            title,
            meta: [
                {
                    hid: 'description',
                    name: 'description',
                    content: description
                },
                {
                    hid: 'og:description',
                    property: 'og:description',
                    content: description
                },
                {
                    hid: 'twitter:description',
                    name: 'twitter:description',
                    content: description
                },
                {
                    hid: 'og:title',
                    property: 'og:title',
                    content: title
                },
                {
                    hid: 'twitter:title',
                    name: 'twitter:title',
                    content: title
                },
                {
                    hid: 'og:image',
                    property: 'og:image',
                    content: this.image.url
                },
                {
                    hid: 'twitter:image',
                    name: 'twitter:image',
                    content: this.image.url
                }
            ]
        }
    },
    computed: {
        ...mapGetters({
            user: 'auth/user'
        })
    },
    watch: {
        async user (newUser, oldUser) {
            await this.setUserFields()
        }
    },
    mounted () {
        this.setUserFields()
    },
    methods: {
        startLoadQueue () {
            if (!this.loadQueue.started) {
                this.loadPrices()

                this.loadQueue.started = true
            }
        },
        loadPrices () {
            if (!this.loadQueue.prices) {
                this.$refs.pricePicker.getLicences()

                this.loadQueue.prices = true
            }
        },
        loadStockInAlbum () {
            if (!this.loadQueue.inAlbum) {
                this.$refs.stockImage.$refs.stockFromShoot.fetchImages()

                this.loadQueue.inAlbum = true
            }
        },
        loadSimilarStock () {
            if (!this.loadQueue.similar) {
                this.$refs.similarStock.fetchImages()

                this.loadQueue.similar = true
            }
        },
        setUserFields () {
            if (this.user) {
                this.contactForm.name = `${this.user.first_name} ${this.user.last_name}`
                this.contactForm.email = this.user.email
            }

            this.contactForm.image_code = this.image.code
        },
        showContact (type) {
            this.contactForm.type = type

            this.contactFormHeading = 'Quote Request'
            this.contactFormPlaceholder = 'Please provide information to help use provide an accurate quote.'

            this.contactForm.name = ''
            this.contactForm.email = ''
            this.contactForm.message = ''

            this.$bvModal.show('modal-exclusive-use')
        },
        async sendExclusiveUseRequest () {
            await this.contactForm.post('/contact/exclusive-timeframe').then((data) => {
                this.$toast.success('Thanks for reaching out! We will get back to you as soon as we can')
                this.$bvModal.hide('modal-exclusive-use')
            }).catch((error) => {
                if (error.response) {
                    this.$toasted.error(error.response.data.message)
                } else {
                    this.$toasted.error(error)
                }
            })
        }
    }

}
</script>
